import { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Gauge,
  Factory,
  Wrench,
} from "lucide-react";
import { Box, Typography, Link, Button, Container } from "@mui/joy";
import { PRODUCTS, getProduct } from "../data/products";
import { useSEO, ORG_JSONLD } from "../lib/seo";
import { Reveal, MaskedLines } from "../site/Reveal";
import { CertMarquee } from "../site/CertMarquee";
import { CTASection } from "../site/CTASection";
import ManufacturingProcess from "../components/ManufacturingProcess";
import Industries from "../components/Industries";
import EngineeringServices from "../components/EngineeringServices";
import ProductFAQ from "../components/ProductFAQ";
const allFaqs = PRODUCTS.flatMap((product) =>
  product.faqs.map((faq) => ({
    ...faq,
    product: product.short, // optional: include product name
  })),
);
const COLORS = {
  obsidian: "#121212",
  safety: "#ff6b00", // Standard safety orange/yellow accent
  concrete: "#f5f5f5", // Light gray background
  ink: "#1a1a1a", // Deep dark text
  steel: "#708090",
  muted: "#666666",
  borderLight: "rgba(26, 26, 26, 0.1)", // border-ink/10
  borderMedium: "rgba(26, 26, 26, 0.15)", // border-ink/15
};
const themeColors = {
  obsidian: "#0b0f19",
  safety: "#f97316", // fallback orange/safety color
  ink: "#111827",
  concrete: "#f3f4f6",
  mutedForeground: "#6b7280",
};

const TRUST = [
  {
    icon: ShieldCheck,
    label: "BIS / ISI Certified",
    sub: "Built to ISI 9815:1994",
  },
  { icon: Gauge, label: "±1% Regulation", sub: "Precision voltage control" },
  { icon: Factory, label: "Custom-Built", sub: "Engineered to your load" },
  {
    icon: Wrench,
    label: "After-Sales Service",
    sub: "Install · commission · maintain",
  },
];

const MANIFESTO = [
  {
    n: "01",
    t: "Precision",
    d: "Regulated output within ±1% — internally settable from 0.5% to 2.0% — with no overshoot, undershoot or waveform distortion.",
  },
  {
    n: "02",
    t: "Protection",
    d: "Isolation, spike suppression and trip protection that shield sensitive electronics from noisy, unstable power lines.",
  },
  {
    n: "03",
    t: "Endurance",
    d: "High-grade lamination and enameled electrolytic copper windings, rugged synchronous motors — engineered for a long service life.",
  },
  {
    n: "04",
    t: "Compliance",
    d: "Every unit conforms to Indian standards — ISI 9815:1994, IS:2026 and IS:335 — for documented, dependable quality.",
  },
];

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useSEO({
    title:
      "ITL Technical Services | Servo Voltage Stabilizer & Transformer Manufacturer India",
    description:
      "BIS/ISI certified manufacturer of servo controlled AC voltage stabilizers, super isolation transformers and constant voltage transformers (CVTs). Custom-built single & three phase power conditioning equipment in India.",
    jsonLd: ORG_JSONLD,
  });

  return (
    <>
      {/* HERO */}
      <Box
        component="section"
        ref={heroRef}
        data-testid="hero"
        className="grain grain-dark"
        sx={{
          position: "relative",
          minHeight: "100svh",
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
          backgroundColor: themeColors.obsidian,
          color: "common.white",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
          }}
        >
          <Box
            component="img"
            src="/HeroSectionHomePage.jpeg"
            alt="Industrial servo voltage stabilizer manufacturing facility"
            sx={{
              width: "100%",
              height: "120%",
              objectFit: "cover",
              opacity: 0.4,
              filter: "grayscale(100%)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(to top, ${themeColors.obsidian}, ${themeColors.obsidian}b3, ${themeColors.obsidian}4d)`,
            }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: "80rem",
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 },
            pb: { xs: 8, md: 12 },
            pt: 16,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: {
                  xs: "1.5rem",
                  sm: "1.75rem",
                  md: "1.75rem",
                  lg: "1.75rem",
                },
                fontWeight: 600,
                mb: 4,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                textAlign: "left",
                background: "linear-gradient(90deg, #ff1744 0%, #2962ff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              ITL Technical Service
            </Typography>

            <Typography
              sx={{
                fontFamily: "monospace",
                color: themeColors.safety,
                mb: 4,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                textAlign: "left",
              }}
            >
              [ Power Conditioning · Manufacturer & Supplier ]
            </Typography>

            <Typography
              level="h1"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "13vw", md: "6rem", lg: "7.5rem" },
                lineHeight: 0.85,
                color: "common.white",
                textAlign: "left",
                width: "100%",
              }}
            >
              <MaskedLines
                lines={["Stable power.", "Zero compromise."]}
                delay={0.3}
              />
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
              gap: 4,
              alignItems: "flex-end",
            }}
          >
            <Typography
              sx={{
                gridColumn: { md: "span 6" },
                color: "rgba(255,255,255,0.7)",
                fontSize: { xs: "1rem", md: "1.125rem" },
                maxWidth: "36rem",
                textAlign: "left",
              }}
            >
              Servo controlled voltage stabilizers, super isolation transformers
              and constant voltage transformers — precision-engineered and
              BIS/ISI certified for demanding industrial loads.
            </Typography>

            <Box
              sx={{
                gridColumn: { md: "span 6" },
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: { md: "flex-end" },
              }}
            >
              <Link
                component={RouterLink}
                to="/contact"
                data-testid="hero-quote-cta"
                underline="none"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  backgroundColor: themeColors.safety,
                  color: "common.white",
                  px: 4,
                  py: 2,
                  fontFamily: "monospace",
                  textTransform: "uppercase",
                  transition: "all 0.2s",
                  "&:hover": {
                    gap: 2.5,
                    color: "common.white",
                  },
                }}
              >
                Request a Quote
                <ArrowRight style={{ width: 16, height: 16 }} />
              </Link>

              <Link
                component={RouterLink}
                to="/products"
                data-testid="hero-products-cta"
                underline="none"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "common.white",
                  px: 4,
                  py: 2,
                  fontFamily: "monospace",
                  textTransform: "uppercase",
                  transition: "color 0.2s",
                  "&:hover": {
                    borderColor: themeColors.safety,
                    color: themeColors.safety,
                  },
                }}
              >
                View Products
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
      <CertMarquee />
      {/* OVERVIEW + TRUST */}
      <Box
        component="section"
        data-testid="overview"
        sx={{
          maxWidth: "80rem",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 10, md: 16 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
            gap: 6,
          }}
        >
          <Box sx={{ gridColumn: { md: "span 6" } }}>
            <Reveal>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  color: themeColors.safety,
                  mb: 3,
                  textTransform: "uppercase",
                  textAlign: "left",
                }}
              >
                [ Who we are ]
              </Typography>
              <Typography
                level="h2"
                sx={{
                  fontFamily: "Cabinet Grotesk, system-ui, sans-serif;",
                  fontWeight: 900,
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  letterSpacing: "-0.05em",
                  color: themeColors.ink,
                  lineHeight: 0.95,
                  textAlign: "left",
                }}
              >
                An established industrial manufacturer of power-protection
                equipment.
              </Typography>
            </Reveal>
          </Box>
          <Box
            sx={{
              gridColumn: { md: "span 6" },
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Reveal delay={0.15}>
              <Typography
                sx={{
                  color: themeColors.mutedForeground,
                  fontSize: "1.125rem",
                  lineHeight: 1.625,
                  textAlign: "left",
                }}
              >
                ITL Technical Services designs, manufactures and services servo
                stabilizers, isolation transformers and CVTs for plant
                engineers, procurement teams and OEMs. We combine decades of
                manufacturing expertise with rigorous compliance to Indian
                standards — delivering equipment that protects your assets and
                runs for years.
              </Typography>
            </Reveal>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
            gap: "1px",
            backgroundColor: `${themeColors.ink}26`, // roughly /15 opacity
            border: `1px solid ${themeColors.ink}26`,
            mt: 8,
          }}
        >
          {TRUST.map((t, i) => (
            <Reveal
              key={t.label}
              delay={i * 0.08}
              style={{ height: "100%", backgroundColor: themeColors.concrete }}
            >
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  height: "100%",
                  transition: "background-color 0.2s",
                  "&:hover": { backgroundColor: "common.white" },
                }}
              >
                <t.icon
                  style={{
                    width: 32,
                    height: 32,
                    color: themeColors.safety,
                    marginBottom: 24,
                  }}
                  strokeWidth={1.75}
                />
                <Typography
                  level="h3"
                  sx={{
                    fontFamily: "var(--font-display, sans-serif)",
                    fontWeight: 700,
                    fontSize: "1.125rem",
                    color: themeColors.ink,
                  }}
                >
                  {t.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: themeColors.mutedForeground,
                    mt: 0.5,
                  }}
                >
                  {t.sub}
                </Typography>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Box>
      {/* PRODUCT CATEGORY CARDS (BENTO) */}
      <Box
        component="section"
        data-testid="products-grid"
        className="grain grain-dark"
        sx={{
          backgroundColor: themeColors.obsidian,
          color: "common.white",
        }}
      >
        <Box
          sx={{
            maxWidth: "80rem",
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 10, md: 16 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 3,
              mb: 8,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  color: themeColors.safety,
                  mb: 3,
                  textTransform: "uppercase",
                  textAlign: "left",
                }}
              >
                [ What we build ]
              </Typography>

              <Typography
                level="h2"
                sx={{
                  fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                  fontWeight: 900,
                  fontSize: { xs: "2.25rem", md: "3.75rem" },
                  letterSpacing: "-0.05em",
                  lineHeight: 0.9,
                  color: "common.white",
                  textAlign: "left",
                }}
              >
                Product categories
              </Typography>
            </Box>

            <Button
              component={RouterLink}
              to="/products"
              variant="outlined"
              size="sm"
              sx={{
                alignSelf: { xs: "flex-start", md: "center" },

                borderColor: themeColors.safety,
                color: themeColors.safety,

                px: { xs: 2.5, md: 4 },
                py: { xs: 0.8, md: 1.3 },

                fontSize: { xs: "0.75rem", md: "0.9rem" },
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: { xs: "0.04em", md: "0.08em" },

                minHeight: { xs: "36px", md: "44px" },
                whiteSpace: "nowrap",

                transition: "all 0.2s ease",

                "&:hover": {
                  backgroundColor: themeColors.safety,
                  color: themeColors.obsidian,
                  borderColor: themeColors.safety,
                },
              }}
            >
              Show All Products
            </Button>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {PRODUCTS.map((p) => (
              <Link
                key={p.slug}
                component={RouterLink}
                to={`/products/${p.slug}`}
                underline="none"
                data-testid={`product-card-${p.slug}`}
                sx={{
                  display: "block",
                  position: "relative",
                  border: "1px solid rgba(255,255,255,0.15)",
                  backgroundColor: "rgba(255,255,255,0.03)",
                  overflow: "hidden",
                  height: "100%",
                  transition: "border-color 0.2s ease",

                  "&:hover": {
                    borderColor: themeColors.safety,
                  },

                  "&:hover .bento-img": {
                    filter: "grayscale(0%)",
                    transform: "scale(1.03)",
                  },

                  "&:hover .bento-title": {
                    color: themeColors.safety,
                  },

                  "&:hover .bento-icon": {
                    color: themeColors.safety,
                    transform: "rotate(45deg)",
                  },
                }}
              >
                <Box
                  sx={{
                    aspectRatio: "16/10",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    className="bento-img"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "grayscale(100%)",
                      transition: "transform 0.35s ease, filter 0.35s ease",
                      willChange: "transform",
                    }}
                  />
                </Box>

                <Box sx={{ p: { xs: 3, md: 4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          color: themeColors.safety,
                          mb: 1,
                          textAlign: "left",
                        }}
                      >
                        [{p.index}]
                      </Typography>

                      <Typography
                        className="bento-title"
                        level="h3"
                        sx={{
                          fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                          fontWeight: 700,
                          fontSize: { xs: "1rem", md: "1.5rem" },
                          letterSpacing: "-0.025em",
                          color: "common.white",
                          lineHeight: 1.1,
                          mb: 1,
                          transition: "color 0.2s ease",
                          textAlign: "left",
                        }}
                      >
                        {p.name}
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: "0.875rem",
                          fontFamily: "monospace",
                          lineHeight: 1.5,
                          textAlign: "left",
                        }}
                      >
                        {p.tagline}
                      </Typography>
                    </Box>

                    <ArrowUpRight
                      className="bento-icon"
                      style={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        transition: "transform 0.2s ease, color 0.2s ease",
                        color: "rgba(255,255,255,0.4)",
                      }}
                    />
                  </Box>
                </Box>
              </Link>
            ))}
          </Box>
        </Box>
      </Box>
      {/* MANIFESTO / WHY CHOOSE US */}
      <Box
        component="section"
        data-testid="manifesto"
        sx={{
          maxWidth: "80rem",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 10, md: 16 },
        }}
      >
        <Reveal>
          <Typography
            sx={{
              fontFamily: "monospace",
              color: themeColors.safety,
              mb: 3,
              textTransform: "uppercase",
              textAlign: "left",
            }}
          >
            [ Why choose us ]
          </Typography>
          <Typography
            level="h2"
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif;",
              textAlign: "left",
              fontWeight: 900,
              fontSize: { xs: "2.25rem", md: "3.75rem" },
              letterSpacing: "-0.05em",
              color: themeColors.ink,
              lineHeight: 0.9,
              maxWidth: "48rem",
              mb: { xs: 8, md: 12 },
            }}
          >
            Engineered on four principles.
          </Typography>
        </Reveal>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
                  gap: 3,
                  py: { xs: 5, md: 7 },
                  borderTop: `1px solid ${themeColors.ink}26`,
                  "&:last-child": {
                    borderBottom: `1px solid ${themeColors.ink}26`,
                  },
                  "&:hover .manifesto-title": { color: themeColors.safety },
                }}
              >
                <Box sx={{ gridColumn: { md: "span 2" } }}>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      color: themeColors.safety,
                      fontSize: "1.125rem",
                      textAlign: "left",
                    }}
                  >
                    [ {m.n} ]
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: { md: "span 4" } }}>
                  <Typography
                    className="manifesto-title"
                    level="h3"
                    sx={{
                      fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                      fontWeight: 900,
                      fontSize: { xs: "1.875rem", md: "3rem" },
                      letterSpacing: "-0.05em",
                      color: themeColors.ink,
                      transition: "color 0.2s",
                      textAlign: "left",
                    }}
                  >
                    {m.t}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    gridColumn: { md: "span 6" },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: themeColors.mutedForeground,
                      fontSize: "1.125rem",
                      lineHeight: 1.625,
                      textAlign: "left",
                    }}
                  >
                    {m.d}
                  </Typography>
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Box>
      <ManufacturingProcess />
      <Industries />
      <EngineeringServices />
      <Box
        component="section"
        data-testid="faq-section"
        sx={{
          backgroundColor: COLORS.concrete,
          borderTop: `1px solid ${COLORS.borderLight}`,
          borderBottom: `1px solid ${COLORS.borderLight}`,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Reveal>
            <Typography
              component="p"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: COLORS.safety,
                mb: 3,
                textAlign: "left",
              }}
            >
              [ FAQ ]
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "1.875rem", md: "3rem" },
                letterSpacing: "-0.03em",
                color: COLORS.ink,
                mb: 5,
                textAlign: "left",
              }}
            >
              Common questions
            </Typography>
          </Reveal>
          <Reveal delay={0.1}>
            <ProductFAQ faqs={allFaqs} />
          </Reveal>
        </Container>
      </Box>{" "}
      <CTASection />
    </>
  );
}
